import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getStoredDateFromExpSeconds, normalizeStoredDateMs } from 'src/helpers/utils';
import { refreshTokenRequest } from 'src/services/authorizationApi';
import { signIn } from 'src/store/user/actions';
import { RootState } from 'src/types/store-types';
import { ONE_HOUR } from 'src/utils/constants';
import { useConnectedAction } from './use-connected-action';

export const useRefreshToken = (): void => {
  const { user } = useSelector((state: RootState) => state.user);
  const _setUserData = useConnectedAction(signIn.success);
  const refreshTimeoutRef = useRef<number | null>(null);

  const clearRefreshTimeout = (): void => {
    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
      refreshTimeoutRef.current = null;
    }
  };

  const setNewStorageValues = (newUser: any, exp: number | string): number => {
    const storedDate = getStoredDateFromExpSeconds(exp);
    localStorage.setItem('user', JSON.stringify(newUser));
    localStorage.setItem('storedDate', storedDate.toString());
    return storedDate;
  };

  const refreshGdLibData = async (token: string): Promise<void> => {
    const gdLib = window.__gdlib;
    if (!gdLib) return;

    try {
      gdLib.login(token);
    } catch (error) {
      console.error('Failed to refresh gdlib data', error);
    }
  };

  const refreshToken = async (currentUser: typeof user): Promise<void> => {
    const res = await refreshTokenRequest();
    const newUser = { ...currentUser, token: res.result.token };

    const storedDate = setNewStorageValues(newUser, res.result.exp);
    _setUserData({ ...newUser, storedDate });
    scheduleTokenRefresh(newUser, res.result.exp);
    await refreshGdLibData(res.result.token);
  };

  const scheduleTokenRefresh = (currentUser: typeof user | null, exp?: number | string): void => {
    const storedDateMs = exp
      ? getStoredDateFromExpSeconds(exp)
      : normalizeStoredDateMs(localStorage.getItem('storedDate'));
    if (!storedDateMs) return;

    const remainingMs = storedDateMs - Date.now();
    if (remainingMs <= 0) return;

    const minBufferMs = 60 * 1000;
    const refreshBufferMs = Math.min(ONE_HOUR, Math.max(minBufferMs, Math.floor(remainingMs * 0.01)));
    const refreshInMs = Math.max(0, remainingMs - refreshBufferMs);

    clearRefreshTimeout();

    refreshTimeoutRef.current = window.setTimeout(() => {
      if (!currentUser) return;
      refreshToken(currentUser).catch((error) => {
        console.error('Failed to refresh token', error);
      });
    }, refreshInMs);
  };

  useEffect(() => {
    if (!user) {
      clearRefreshTimeout();
      return;
    }

    scheduleTokenRefresh(user);

    return () => {
      clearRefreshTimeout();
    };
  }, [user]);
};
